import { useOrganizationList, useUser } from '@clerk/clerk-react'
import type { UserResource } from '@clerk/types'
import { type ReactNode, useRef } from 'react'
import { Navigate } from 'react-router-dom'

import {
  readOnboardingStatus,
  shouldShowWizard,
  updateOnboardingMetadata,
} from '@/components/onboarding/onboarding-metadata.helper'
import type { DeepLinkIntent } from '@/lib/deep-link-intent.util'
import {
  buildDeepLinkChatUrl,
  consumeDeepLinkIntent,
  markDeepLinkArrivedInSession,
} from '@/lib/deep-link-intent.util'
import { rootRoutes } from '@/routes/org-routes.const'

import { LoadingScreen } from './loading-screen.atom'

interface MembershipsLike {
  isLoading: boolean
  data: unknown
}

const resolveRedirectTarget = (
  orgSlug: string | null | undefined,
  intent: DeepLinkIntent | null
): string => {
  if (!orgSlug) return `/${rootRoutes.onboarding}`
  if (intent) return buildDeepLinkChatUrl(orgSlug, intent)
  return `/${orgSlug}`
}

const isReadyForRedirect = (
  isLoaded: boolean,
  user: UserResource | null | undefined,
  userMemberships: MembershipsLike | null | undefined
): boolean =>
  isLoaded && !!user && !!userMemberships && !userMemberships.isLoading && !!userMemberships.data

const handleDeepLinkArrival = (
  user: UserResource | null | undefined,
  intent: DeepLinkIntent | null
): void => {
  if (!user || !intent) return
  markDeepLinkArrivedInSession()
  const status = readOnboardingStatus(user.unsafeMetadata)
  if (shouldShowWizard(status)) {
    void updateOnboardingMetadata(user, 'skipped')
  }
}

export const OrgRedirect = (): ReactNode => {
  const { isLoaded, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  })
  const { user } = useUser()
  const intentRef = useRef<DeepLinkIntent | null | undefined>(undefined)

  if (!isReadyForRedirect(isLoaded, user, userMemberships)) return <LoadingScreen />

  const memberships = userMemberships.data ?? []
  if (memberships.length === 0) {
    // Keep the deep-link intent in localStorage — consuming it here would
    // clear it before onboarding completes, and the user would land on the
    // default dashboard instead of the shared chat page after org creation.
    return <Navigate to={`/${rootRoutes.onboarding}`} replace />
  }

  if (intentRef.current === undefined) {
    intentRef.current = consumeDeepLinkIntent()
    handleDeepLinkArrival(user, intentRef.current)
  }

  const orgSlug = memberships[0]?.organization?.slug
  return <Navigate to={resolveRedirectTarget(orgSlug, intentRef.current)} replace />
}
